import { __spreadArray } from "tslib";
import { useContentShareState, useMeetingManager, useRosterState } from 'amazon-chime-sdk-component-library-react';
import { useEffect, useState, useRef } from 'react';
import { useVideoConfig } from '../components/video/video-config-provider';
var MOVING_AVERAGE_LENGTH = 10;
var DEGRADE_FREQUENCY = 1000;
var MIN_FOCUS_TIME = 3000;
var UPDATE_FOCUS_INTERVAL = 500;
export var useAttendees = function () {
    var _a, _b, _c;
    var currentAttendeeId = (_b = (_a = useMeetingManager().configuration) === null || _a === void 0 ? void 0 : _a.credentials) === null || _b === void 0 ? void 0 : _b.attendeeId;
    var sharingAttendeeId = (_c = useContentShareState().sharingAttendeeId) === null || _c === void 0 ? void 0 : _c.replace('#content', '');
    var _d = useVideoConfig(), knownClients = _d.knownClients, webinar = _d.webinar;
    var roster = useRosterState().roster;
    var attendees = [];
    var hostAttendeeIds = knownClients.filter(function (_a) {
        var isHost = _a.isHost;
        return isHost;
    }).map(function (_a) {
        var clientId = _a.clientId;
        return clientId;
    });
    if (webinar) {
        attendees.push.apply(attendees, __spreadArray([sharingAttendeeId], hostAttendeeIds, false));
    }
    else {
        attendees.push.apply(attendees, __spreadArray([currentAttendeeId, sharingAttendeeId], Object.keys(roster), false));
    }
    var visibleAttendees = attendees
        .filter(function (attendee) { return knownClients.find(function (_a) {
        var clientId = _a.clientId, isHidden = _a.isHidden;
        return clientId === attendee && !isHidden;
    }); })
        .filter(function (attendeeId, index, self) { return attendeeId && self.indexOf(attendeeId) === index; });
    var focusedAttendeeId = useFocusedAttendee(visibleAttendees, [currentAttendeeId]);
    return {
        focusedAttendeeId: sharingAttendeeId || focusedAttendeeId,
        currentAttendeeId: currentAttendeeId,
        sharingAttendeeId: sharingAttendeeId,
        visibleAttendees: visibleAttendees,
    };
};
var useFocusedAttendee = function (allAttendees, ignoredAttendees) {
    if (ignoredAttendees === void 0) { ignoredAttendees = []; }
    var attendees = allAttendees.filter(function (attendee) { return !ignoredAttendees.includes(attendee); });
    var meetingManager = useMeetingManager();
    var scoresRef = useRef({});
    var _a = useState(attendees[0]), focusedAttendee = _a[0], setFocusedAttendee = _a[1];
    var focusedTimeRef = useRef(0);
    useEffect(function () {
        var updateScoresInterval = setInterval(function () {
            var now = new Date().getTime();
            if (now - focusedTimeRef.current >= MIN_FOCUS_TIME) {
                var recordScore_1 = 0;
                var recordAttendee_1 = focusedAttendee;
                Object.entries(scoresRef.current).forEach(function (_a) {
                    var attendee = _a[0], score = _a[1];
                    if (score > recordScore_1) {
                        recordScore_1 = score;
                        recordAttendee_1 = attendee;
                    }
                });
                if (recordAttendee_1 !== focusedAttendee) {
                    setFocusedAttendee(recordAttendee_1);
                    focusedTimeRef.current = now;
                }
            }
        }, UPDATE_FOCUS_INTERVAL);
        return function () {
            clearInterval(updateScoresInterval);
        };
    }, []);
    useEffect(function () {
        var degraderInterval = setInterval(function () {
            scoresRef.current = degradeScores(scoresRef.current);
        }, DEGRADE_FREQUENCY);
        return function () {
            clearInterval(degraderInterval);
        };
    }, []);
    useEffect(function () {
        scoresRef.current = clearInvisibleScores(scoresRef.current, attendees);
        if (!focusedAttendee) {
            setFocusedAttendee(attendees[0]);
            focusedTimeRef.current = new Date().getTime();
        }
        var destructors = [];
        attendees.forEach(function (attendee) {
            var handler = function (attendeeId, volume, muted) {
                if (muted) {
                    scoresRef.current = updateScore(scoresRef.current, attendeeId, 0, true);
                    return;
                }
                if (volume === null && !Object.is(scoresRef.current[attendeeId], undefined)) {
                    scoresRef.current = updateScore(scoresRef.current, attendeeId, scoresRef.current[attendeeId]);
                    return;
                }
                scoresRef.current = updateScore(scoresRef.current, attendeeId, volume || 0, muted);
            };
            // For some reason audioVideo might not be ready here yet sometimes
            if (meetingManager.audioVideo) {
                meetingManager.audioVideo.realtimeSubscribeToVolumeIndicator(attendee, handler);
            }
            destructors.push(function () {
                if (meetingManager.audioVideo) {
                    meetingManager.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendee, handler);
                }
            });
        });
        return function () {
            destructors.forEach(function (destructor) { return destructor(); });
        };
    }, [attendees]);
    return focusedAttendee;
};
var updateScore = function (scores, attendeeId, score, force) {
    if (force === void 0) { force = false; }
    if (Object.is(scores[attendeeId], undefined) || force) {
        scores[attendeeId] = score;
    }
    scores[attendeeId] = addValueToMovingAverage(scores[attendeeId], score);
    return scores;
};
var clearInvisibleScores = function (scores, attendees) {
    var filtered = {};
    Object.entries(scores).forEach(function (_a) {
        var attendee = _a[0], score = _a[1];
        if (attendees.includes(attendee)) {
            filtered[attendee] = score;
        }
    });
    return filtered;
};
var degradeScores = function (scores) {
    Object.keys(scores).forEach(function (attendee) {
        scores[attendee] = addValueToMovingAverage(scores[attendee], 0);
    });
    return scores;
};
var addValueToMovingAverage = function (currentValue, newValue) {
    return (currentValue * MOVING_AVERAGE_LENGTH + newValue) / (MOVING_AVERAGE_LENGTH + 1);
};
//# sourceMappingURL=attendees.js.map